import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { useUser } from '../context/UserContext';
import { usePlayerProfile } from '../hooks/playerProfile/usePlayerProfile';
import { useNotification } from '../context/NotificationContext';
import styles from './UserMenu.module.css';
import { FaSignOutAlt, FaBell, FaSearch, FaUser, FaBuilding, FaChalkboardTeacher, FaUserFriends, FaExchangeAlt, FaChevronDown, FaChevronUp, FaCheck } from 'react-icons/fa';

const DASHBOARD_ROLES = [
  { id: 'player', label: 'Player Dashboard', icon: FaUser, description: 'Your athletic journey' },
  { id: 'parent', label: 'Parent Dashboard', icon: FaUserFriends, description: 'Manage your child\'s journey' },
  { id: 'club_owner', label: 'Club Owner Dashboard', icon: FaBuilding, description: 'Manage clubs & programs' },
  { id: 'coach', label: 'Coach Dashboard', icon: FaChalkboardTeacher, description: 'Train & manage teams' }
];

const UserMenu = ({ user }) => {
  const { userProfile, switchDashboard } = useUser();
  const { showSuccess, showError } = useNotification();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDashboardSwitcherOpen, setIsDashboardSwitcherOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  
  // Get current dashboard role
  const currentDashboardRole = userProfile?.dashboardRole || 'player';

  // Use the same hook as PlayerProfile page to fetch player profile
  const { profile: playerProfile, loading: profileLoading } = usePlayerProfile(
    userProfile?._id,
    { sections: 'basicInfo', skipCache: false }
  );

  // Get user initials from PlayerProfile only
  const getUserInitials = () => {
    // Priority 1: Use PlayerProfile firstName and lastName
    if (playerProfile?.firstName && playerProfile?.lastName) {
      return `${playerProfile.firstName[0]}${playerProfile.lastName[0]}`.toUpperCase();
    }
    
    // Priority 2: Use PlayerProfile firstName only
    if (playerProfile?.firstName) {
      return playerProfile.firstName.substring(0, 2).toUpperCase();
    }
    
    // Priority 3: Parse Firebase displayName (fallback)
    if (user?.displayName) {
      const parts = user.displayName.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return user.displayName.substring(0, 2).toUpperCase();
    }
    
    // Priority 4: Use email first letter
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    
    // Fallback
    return 'U';
  };

  // Get user full name from PlayerProfile only
  const getUserName = () => {
    // Priority 1: PlayerProfile firstName + lastName
    if (playerProfile?.firstName && playerProfile?.lastName) {
      return `${playerProfile.firstName} ${playerProfile.lastName}`;
    }
    
    // Priority 2: PlayerProfile firstName only
    if (playerProfile?.firstName) {
      return playerProfile.firstName;
    }
    
    // Priority 3: Firebase displayName (fallback)
    if (user?.displayName) {
      return user.displayName;
    }
    
    // Priority 4: Email username (fallback)
    if (user?.email) {
      return user.email.split('@')[0];
    }
    
    return 'User';
  };

  // Get user profile photo from PlayerProfile only
  const getProfilePhoto = () => {
    // Priority 1: PlayerProfile photo from media (Cloudinary)
    if (playerProfile?.media?.profileImage?.cloudinary?.url) {
      return playerProfile.media.profileImage.cloudinary.url;
    }
    
    // Priority 2: PlayerProfile direct picture
    if (playerProfile?.profilePicture) {
      return playerProfile.profilePicture;
    }
    
    // Priority 3: Firebase photo URL (OAuth providers - fallback only)
    if (user?.photoURL) {
      return user.photoURL;
    }
    
    return null;
  };

  const profilePhoto = getProfilePhoto();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login'); // Navigate to the login page after logout
    } catch (error) {
      console.error('Error logging out:', error.message);
    }
    setIsMenuOpen(false); // Close menu after logout
  };

  const handleMenuClick = (path) => {
    navigate(path);
    setIsMenuOpen(false); // Close menu after navigation
  };

  // Handle dashboard switch
  const handleDashboardSwitch = async (roleId) => {
    if (roleId === currentDashboardRole) {
      setIsDashboardSwitcherOpen(false);
      return;
    }

    setIsSwitching(true);
    try {
      const result = await switchDashboard(roleId);
      if (result.success) {
        showSuccess(result.message || 'Dashboard switched successfully');
        setIsDashboardSwitcherOpen(false);
        setIsMenuOpen(false);
        // Navigate to home to reload dashboard
        navigate('/');
      } else {
        // Ensure error is a string (not an object) to avoid React error #31
        const errorMsg = typeof result.error === 'string' 
          ? result.error 
          : (result.error?.message || 'Failed to switch dashboard');
        showError(errorMsg);
      }
    } catch (error) {
      showError('Failed to switch dashboard');
    } finally {
      setIsSwitching(false);
    }
  };

  // Get current role info
  const currentRoleInfo = DASHBOARD_ROLES.find(r => r.id === currentDashboardRole) || DASHBOARD_ROLES[0];

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={menuRef} className={styles.userMenu}>
      <div onClick={() => setIsMenuOpen(!isMenuOpen)} className={styles.menuTrigger}>
        {profilePhoto ? (
          <img 
            src={profilePhoto} 
            alt={getUserName()} 
            className={styles.profilePhoto}
          />
        ) : (
          <div className={styles.initialsCircle}>
            <span className={styles.userInitials}>{getUserInitials()}</span>
          </div>
        )}
      </div>
      {isMenuOpen && (
        <div className={styles.menuDropdown}>
          <div className={styles.menuHeader}>
            {profilePhoto ? (
              <img 
                src={profilePhoto} 
                alt={getUserName()} 
                className={styles.menuUserAvatar}
              />
            ) : (
              <div className={styles.menuUserAvatar}>
                {getUserInitials()}
              </div>
            )}
            <div className={styles.menuUserInfo}>
              <div className={styles.menuUserName}>{getUserName()}</div>
              <div className={styles.menuUserEmail}>{user?.email}</div>
            </div>
          </div>
          <div className={styles.menuDivider}></div>
          
          {/* Dashboard Switcher */}
          <div className={styles.dashboardSwitcher}>
            <div 
              className={styles.dashboardSwitcherHeader}
              onClick={() => setIsDashboardSwitcherOpen(!isDashboardSwitcherOpen)}
            >
              <div className={styles.currentDashboard}>
                <FaExchangeAlt className={styles.switchIcon} />
                <div className={styles.currentDashboardInfo}>
                  <span className={styles.currentDashboardLabel}>Current View</span>
                  <span className={styles.currentDashboardName}>{currentRoleInfo.label}</span>
                </div>
              </div>
              {isDashboardSwitcherOpen ? <FaChevronUp /> : <FaChevronDown />}
            </div>
            
            {isDashboardSwitcherOpen && (
              <div className={styles.dashboardOptions}>
                {DASHBOARD_ROLES.map(role => {
                  const isCurrent = role.id === currentDashboardRole;
                  const IconComponent = role.icon;
                  
                  return (
                    <div
                      key={role.id}
                      className={`${styles.dashboardOption} ${isCurrent ? styles.currentOption : ''}`}
                      onClick={() => !isSwitching && handleDashboardSwitch(role.id)}
                    >
                      <IconComponent className={styles.dashboardOptionIcon} />
                      <div className={styles.dashboardOptionInfo}>
                        <span className={styles.dashboardOptionLabel}>{role.label}</span>
                        <span className={styles.dashboardOptionDesc}>{role.description}</span>
                      </div>
                      {isCurrent && <FaCheck className={styles.checkIcon} />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className={styles.menuDivider}></div>
          
          <div onClick={() => handleMenuClick(`/player-profile/${userProfile?._id || user?.uid}`)} className={styles.menuItem}>
            <FaUser className={styles.icon} /> My Sports Profile
          </div>
          <div onClick={() => handleMenuClick('/talent-discovery')} className={styles.menuItem}>
            <FaSearch className={styles.icon} /> Find Players
          </div>
          <div onClick={() => handleMenuClick('/notifications')} className={styles.menuItem}>
            <FaBell className={styles.icon} /> Notifications
          </div>
          <div onClick={handleLogout} className={styles.menuItem}>
            <FaSignOutAlt className={styles.icon} /> Logout
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
